import { where } from "firebase/firestore";
import { useCallback, useMemo } from "react";
import { Badge, ListGroup, Stack } from "react-bootstrap";
import { FaChevronRight, FaEyeSlash, FaFileAlt } from "react-icons/fa";
import { Link } from "react-router";

import * as api from "../../api/api";
import { useResultCollection } from "../../api/firestoreWrappers.generated";
import * as missionApi from "../../api/missionApi";
import { useUser } from "../../contexts/user_provider";
import urls from "../../urls";
import MeasurementInfoBadge from "../MeasurementInfo";
import MissionActivityBadge from "../MissionActivityBadge";
import StateIcon from "../StateIcon";
import TimeDisplay from "../TimeDisplay";

function MissionResultsBadge({ mission, className }) {
    const { getUserIsAdmin } = useUser();

    const [results] = useResultCollection(
        mission.organizationId,
        mission.locationId,
        mission.id,
        [where("hasFiles", "==", true)],
    );

    const publishedResults = useMemo(() => {
        return results.flatMap((result) =>
            (result.files ?? [])
                .filter(
                    (file) =>
                        file.published === true && file.name !== undefined,
                )
                .map((file) => ({
                    ...file,
                    organizationId: mission.organizationId,
                    locationId: mission.locationId,
                    missionId: mission.id,
                    resultId: result.id,
                })),
        );
    }, [results, mission.organizationId, mission.locationId, mission.id]);

    const unpublishedResults = useMemo(() => {
        return results.flatMap((result) =>
            (result.files ?? [])
                .filter(
                    (file) =>
                        file.published === false && file.name !== undefined,
                )
                .map((file) => ({
                    ...file,
                    organizationId: mission.organizationId,
                    locationId: mission.locationId,
                    missionId: mission.id,
                    resultId: result.id,
                })),
        );
    }, [results, mission.organizationId, mission.locationId, mission.id]);

    const handleSingleDownload = useCallback((event, items) => {
        if (items.length === 1) {
            event.preventDefault();
            event.stopPropagation();

            const { organizationId, locationId, missionId, resultId, name } =
                items[0];

            api.downloadResult(
                organizationId,
                locationId,
                missionId,
                resultId,
                name,
            );
        }
    }, []);

    const handlePublishedClick = useCallback(
        (event) => {
            handleSingleDownload(event, publishedResults);
        },
        [publishedResults, handleSingleDownload],
    );

    const handleUnpublishedClick = useCallback(
        (event) => {
            handleSingleDownload(event, unpublishedResults);
        },
        [unpublishedResults, handleSingleDownload],
    );

    if (publishedResults.length === 0 && unpublishedResults.length === 0)
        return null;

    return (
        <>
            {publishedResults.length > 0 && (
                <Badge
                    pill
                    bg="success"
                    className={className}
                    onClick={handlePublishedClick}
                >
                    <FaFileAlt className="me-1" />
                    {publishedResults.length}
                </Badge>
            )}
            {getUserIsAdmin() && unpublishedResults.length > 0 && (
                <Badge
                    pill
                    bg="success"
                    className={className}
                    onClick={handleUnpublishedClick}
                >
                    <FaEyeSlash />
                    <FaFileAlt className="me-1" />
                    {unpublishedResults.length}
                </Badge>
            )}
        </>
    );
}

export default function MissionListItem({
    mission,
    timeDisplayFormat,
    showOrganizationName,
    organizations,
    className,
}) {
    function getOrganizationName() {
        const organizationName = organizations?.[mission.organizationId]?.name;
        return (
            <>
                <span className="ms-1">@ </span>
                {organizationName ? (
                    <span>{organizationName}</span>
                ) : (
                    <span className="placeholder w-25 rounded-1" />
                )}
            </>
        );
    }

    return (
        <ListGroup.Item
            className={`px-4 py-2 d-flex justify-content-between align-items-center border-0 border-bottom ${className}`}
            key={mission.id}
            as={Link}
            to={urls.locationMission(
                mission.organizationId,
                mission.locationId,
                mission.id,
            )}
            action
        >
            <div className="placeholder-glow" style={{ width: "100%" }}>
                <div className="h-100">
                    <StateIcon
                        state={missionApi.getMissionState(mission)}
                        className="me-3"
                    />
                    <TimeDisplay
                        timestamp={mission.timestamp.seconds}
                        format={timeDisplayFormat}
                        organizationId={mission.organizationId}
                        locationId={mission.locationId}
                    />
                    {showOrganizationName && getOrganizationName()}
                    {` by ${mission.drone.id}`}
                </div>
            </div>
            <div className="d-flex align-items-center justify-content-end h-100">
                <Stack
                    direction="vertical"
                    className="flex-md-row gap-1 justify-content-end mx-2"
                >
                    <MissionActivityBadge
                        mission={mission}
                        className="d-flex align-items-center justify-content-center"
                    />
                    <MissionResultsBadge
                        className="d-flex align-items-center justify-content-center"
                        mission={mission}
                    />
                    <MeasurementInfoBadge
                        className="d-flex align-items-center justify-content-center"
                        mission={mission}
                        small
                    />
                </Stack>
                <div>
                    <FaChevronRight style={{ color: "#b9b9b9" }} />
                </div>
            </div>
        </ListGroup.Item>
    );
}
